import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle2, ExternalLink, ShieldCheck, XCircle, Loader2, Video, Copy } from "lucide-react";

const SCOPES = [
  "user:read:admin",
  "meeting:write:admin",
  "meeting:write:registrant:admin",
  "meeting:delete:admin",
  "webinar:write:admin (if you use Zoom Webinars)",
  "webinar:write:registrant:admin (if you use Zoom Webinars)",
];

export default function ZoomIntegration() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ account_id: "", client_id: "", client_secret: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/integrations/zoom/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/integrations/zoom/test", form);
      toast.success(`Zoom connected — logged in as ${r.data.zoom_user.email}`);
      setForm({ account_id: "", client_id: "", client_secret: "" });
      load();
    } catch (ex) {
      const d = ex.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Zoom connection failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Zoom? Future webinars will fall back to native 100ms.")) return;
    await api.delete("/integrations/zoom/disconnect");
    toast.success("Zoom disconnected");
    load();
  };

  const copyScope = (s) => { navigator.clipboard.writeText(s.split(" ")[0]); toast.success("Scope copied"); };

  return (
    <div className="space-y-6" data-testid="zoom-integration-page">
      <div>
        <div className="vp-label">Integrations</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-1">Zoom</h1>
        <p className="text-[#4A4D57] text-sm mt-2 max-w-2xl">
          Connect your own Zoom Pro (or Zoom Webinar) account. Har new webinar automatically Zoom pe create hoga, aur har registrant ko personalized Zoom join link milega — sab kuch aapke brand aur aapke domain se.
        </p>
      </div>

      {/* Status card */}
      <div className={"vp-glass p-6 flex items-start gap-4 " + (status?.connected ? "border-2 border-[#22C55E]/40" : "")}>
        {status === null ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#0B5CFF]" />
        ) : status.connected ? (
          <>
            <div className="w-11 h-11 rounded-xl bg-[#22C55E]/15 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6 text-[#22C55E]" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">Zoom is connected</div>
              <div className="text-sm text-[#4A4D57] mt-1">
                Logged in as <span className="font-mono">{status.zoom_email}</span> · Account <span className="font-mono">{status.account_id}</span>
              </div>
              <div className="text-xs text-[#747680] mt-1">Last updated: {status.updated_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
            <button onClick={disconnect} className="vp-btn-ghost text-red-600" data-testid="zoom-disconnect-btn">
              <XCircle className="w-4 h-4" /> Disconnect
            </button>
          </>
        ) : (
          <>
            <div className="w-11 h-11 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center shrink-0">
              <Video className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">Zoom not connected yet</div>
              <div className="text-sm text-[#4A4D57] mt-1">Add your Server-to-Server OAuth credentials below. It's a 5-minute one-time setup.</div>
            </div>
          </>
        )}
      </div>

      {/* Setup instructions */}
      {!status?.connected && (
        <div className="vp-glass p-6 space-y-5">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#0B5CFF]" />
            <h2 className="font-display font-black text-lg">Setup guide (5 minutes)</h2>
          </div>
          <ol className="space-y-3.5 text-sm text-[#0B0D17] leading-relaxed">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0B5CFF] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
              <div>
                Sign in to <a href="https://marketplace.zoom.us/" target="_blank" rel="noreferrer" className="text-[#0B5CFF] hover:underline inline-flex items-center gap-1">Zoom App Marketplace <ExternalLink className="w-3 h-3" /></a> as an <strong>account admin</strong>.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0B5CFF] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
              <div>Click <strong>Develop → Build App → Server-to-Server OAuth</strong>. Name it "Videoepreneur Webinars".</div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0B5CFF] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
              <div>
                Fill basic info + developer contact. Then open <strong>Scopes → Add Scopes</strong> and enable ALL of these:
                <div className="mt-2 grid gap-1.5">
                  {SCOPES.map((s) => (
                    <button key={s} onClick={() => copyScope(s)} className="flex items-center gap-2 text-left px-2.5 py-1.5 rounded-lg bg-[#F6F7FA] hover:bg-[#E4E6EC] font-mono text-xs text-[#0B0D17]">
                      <Copy className="w-3 h-3 text-[#747680]" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0B5CFF] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>
              <div><strong>Activate</strong> the app. Inactive apps won't issue tokens.</div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0B5CFF] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">5</span>
              <div>
                Open <strong>App Credentials</strong> tab and copy:
                <ul className="mt-1 text-xs text-[#4A4D57] pl-4 list-disc">
                  <li>Account ID</li>
                  <li>Client ID</li>
                  <li>Client Secret (only shown once — save it now)</li>
                </ul>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#0B5CFF] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">6</span>
              <div>Paste them in the form below and hit <strong>Test & Save</strong>.</div>
            </li>
          </ol>
        </div>
      )}

      {/* Connect form */}
      {!status?.connected && (
        <form onSubmit={submit} className="vp-glass p-6 space-y-4" data-testid="zoom-connect-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="vp-label">Account ID <span className="text-red-500">*</span></span>
              <input required className="vp-input" placeholder="abc12XYZ_defgHI34" autoComplete="off" value={form.account_id} onChange={set("account_id")} data-testid="zoom-account-id" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="vp-label">Client ID <span className="text-red-500">*</span></span>
              <input required className="vp-input" placeholder="pKq…" autoComplete="off" value={form.client_id} onChange={set("client_id")} data-testid="zoom-client-id" />
            </label>
            <label className="flex flex-col gap-1.5 sm:col-span-2">
              <span className="vp-label">Client Secret <span className="text-red-500">*</span></span>
              <input required type="password" className="vp-input" placeholder="••••••••••••••••" autoComplete="new-password" value={form.client_secret} onChange={set("client_secret")} data-testid="zoom-client-secret" />
              <span className="text-xs text-[#747680]">Secret is encrypted at rest with Fernet. Only your backend can decrypt it.</span>
            </label>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} className="vp-btn-primary inline-flex items-center gap-2" data-testid="zoom-connect-submit">
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Testing…</>) : (<><ShieldCheck className="w-4 h-4" /> Test & Save</>)}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
